import pluralize from 'pluralize';
import sortBy from 'lodash/sortBy';

import { AssessmentStimulusFragment_stimulusResources } from 'approot/assessment/sections/stimulus/__generated__/AssessmentStimulusFragment';
import { LessonStimulusFragment_stimulusResources } from 'approot/lesson/sections/stimulus/__generated__/LessonStimulusFragment';
import { StudentAssessmentStimulusFragment_stimulusResources } from 'approot/student-assessment/sections/stimulus/__generated__/StudentAssessmentStimulusFragment';
import { StudentLessonStimulusFragment_stimulusResources } from 'approot/student-lesson/sections/stimulus/__generated__/StudentLessonStimulusFragment';
import {
  EVENT_CATEGORY_CONTENT,
  EVENT_TARGET_TYPE_STIMULUS,
  EVENT_ACTION_DOWNLOAD,
  EVENT_ACTION_EXTERNAL_LINK,
} from 'approot/shared/data-tracker/data-events.constants';
import {
  DATA_TRACKER_MANUAL_PAGEVIEW_CONTENT_TYPES,
  DATA_TRACKER_DOWNLOADABLE_STIMULUS_RESOURCES,
  DATA_TRACKER_ENCODE_SINGLE_QUOTE,
} from 'approot/shared/data-tracker/data-tracker.constants';
import { Props as ContentCardProps } from 'approot/shared/content-card/content-card.constants';
import { StimulusContentItem } from './stimulus.constants';
import { StimulusResource } from './__generated__/StimulusResource';
import {
  ASSESSMENT_ACTIVITIES,
  LESSON,
  LESSON_ACTIVITIES,
  StimulusResourceAttachmentType,
} from 'global/constants';
import { dataTrackerEncodeValue } from 'approot/shared/data-tracker/data-tracker.utils';

export const getStimulusViewDataTrackingAttributes = (
  content: StimulusContentItem
): Pick<
  ContentCardProps,
  'dataEvent' | 'dataEventAction' | 'dataEventLabel'
> => {
  if (DATA_TRACKER_MANUAL_PAGEVIEW_CONTENT_TYPES.includes(content.type)) {
    return {
      dataEvent: EVENT_CATEGORY_CONTENT,
      dataEventAction: EVENT_ACTION_EXTERNAL_LINK,
      dataEventLabel: {
        target_type: EVENT_TARGET_TYPE_STIMULUS,
        target_identifier: content.id,
        link: dataTrackerEncodeValue({
          value: getStimulusResourceLink(content) || '',
          encoderFunction: DATA_TRACKER_ENCODE_SINGLE_QUOTE,
        }),
      },
    };
  } else if (
    DATA_TRACKER_DOWNLOADABLE_STIMULUS_RESOURCES.includes(content.type)
  ) {
    return {
      dataEvent: EVENT_CATEGORY_CONTENT,
      dataEventAction: EVENT_ACTION_DOWNLOAD,
      dataEventLabel: {
        target_type: EVENT_TARGET_TYPE_STIMULUS,
        target_identifier: content.id,
        target_subtype: content.type,
      },
    };
  }

  return {};
};

export function getStimulusResourceLink(
  item: LessonStimulusFragment_stimulusResources
) {
  return 'link' in item ? item.link || undefined : undefined;
}

export function getStimulusExtraInfo(
  item:
    | LessonStimulusFragment_stimulusResources
    | AssessmentStimulusFragment_stimulusResources
    | StudentLessonStimulusFragment_stimulusResources
    | StudentAssessmentStimulusFragment_stimulusResources
) {
  if (!item) return undefined;

  if ('image' in item && item.image?.classification) {
    return item.image.classification;
  }

  if ('links' in item) {
    return pluralize('link', item.links.length, true);
  }

  if ('durationMinutes' in item && (item?.durationMinutes || 0) > 0) {
    return pluralize('minute', item.durationMinutes || 0, true);
  }

  if ('totalPages' in item) {
    return pluralize('page', item.totalPages || 0, true);
  }

  return undefined;
}

export function sortAndDeduplicateStimulus(
  stimulusResources: StimulusResource[]
) {
  const sortedData = sortBy([...stimulusResources], 'sort');
  return sortedData.filter(
    (item, index, self) => index === self.findIndex(obj => obj.id === item.id)
  );
}

export function splitPreviousAndCurrentStimulus({
  stimulusResources,
  parentType,
  hasActivities,
}: {
  stimulusResources: StimulusResource[];
  parentType: StimulusResourceAttachmentType;
  hasActivities: boolean;
}) {
  let currentStimulus: StimulusResource[] = [];
  let previousStimulus: StimulusResource[] = [];

  if (!hasActivities)
    return {
      currentStimulus: stimulusResources,
      previousStimulus: [],
    };

  stimulusResources.forEach(s => {
    if (
      s.contentTypes?.includes(
        parentType === LESSON ? LESSON_ACTIVITIES : ASSESSMENT_ACTIVITIES
      )
    ) {
      currentStimulus.push(s);
    } else {
      previousStimulus.push(s);
    }
  });

  return {
    currentStimulus,
    previousStimulus,
  };
}
